import { styled } from "styled-components";

import { triggerLogin } from "authentication/actions";
import { UIButton } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { useWeforumUrl } from "common/utilities/useWeforumUrl";
import type { TrackEvent, TrackPage } from "domains/analytics/types";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { useGoToMembership } from "entities/user/utils";

const StyledContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 5px;
  padding: 1rem;
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.05);
`;

const StyledText = styled.p`
  margin: 0 0 22px 0;
  color: ${({ theme }) => theme.colors.textBright};
  font-size: 1rem;
  line-height: 1.5rem;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
`;

const StyledButtonText = styled.div`
  color: ${({ theme }) => theme.colors.blue};
  margin-right: 10px;
  font-weight: 500;
  cursor: pointer;
  display: inline-block;
`;

type NoAccessSidebarProps = { text?: string; trackPage: TrackPage };

export const NoAccessSidebar = ({ trackPage, text }: NoAccessSidebarProps) => {
  const dispatch = useDispatch();
  const goToMembership = useGoToMembership();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const track = useTrack();
  const translate = useTranslate();
  const weforumUrl = useWeforumUrl();

  const onLoginRegisterHandle = (name: TrackEvent) => () => {
    track({ name, eventType: "initialize modal" });
    dispatch(triggerLogin());
  };

  const onMembershipHandle = () => {
    track({ name: "Upgrade", page: trackPage, subpage: "Message banner" });
    goToMembership();
  };

  return (
    <StyledContainer>
      <StyledText>{text ?? translate("app.monitor.unauthorized_description")}</StyledText>
      {isAuthenticated ? (
        <StyledButtonText onClick={onMembershipHandle}>{translate("app.join_forum_community.title")}</StyledButtonText>
      ) : (
        <StyledButtonContainer>
          <UIButton as="a" href={`${weforumUrl}/join-us`} variant="filled">
            {translate("app.registration.join_us")}
          </UIButton>
          <UIButton variant="outlined" onClick={onLoginRegisterHandle("Login")}>
            {translate("app.user.sign_in")}
          </UIButton>
        </StyledButtonContainer>
      )}
    </StyledContainer>
  );
};
